import {ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, signal, viewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ShareholdersService} from './shareholders.service';
import {PaginationComponent} from '@components/ui/pagination/pagination.component';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {ModalWrapperComponent} from '@components/views/modal-wrapper/modal-wrapper.component';
import {TableTemplateComponentHost} from '@components/ui/table-template/table-template-component-host';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {ShareholdersConverterService} from './view-models/shareholders-converter.service';
import {ShareholdersViewModel} from './view-models/shareholders-view.model';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {ClientShareClassHolder} from '../../../shared/interfaces/clientShareClassHolder';
import {Keys} from '@shared/keys';

@Component({
    selector: 'app-shareholders',
    templateUrl: './shareholders.component.html',
    styleUrls: ['./shareholders.component.scss'],
    providers: [ClientsideDataService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ModalWrapperComponent, LoadingAnimationComponent, TableTemplateComponent]
})
export class ShareholdersComponent implements OnInit, TableTemplateComponentHost {

  private readonly shareholdersService = inject(ShareholdersService);
  private readonly converter = inject(ShareholdersConverterService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly clientsideDataService = inject(ClientsideDataService<ShareholdersViewModel>);
  private readonly destroyRef = inject(DestroyRef);

  private readonly SEARCH_FIELDS: Keys<ShareholdersViewModel> = ['name'];

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);
  readonly columns = signal<TableTemplateColumnConfig[]>([]);
  readonly sortField = signal('name');
  readonly sortOrder = signal(1);
  readonly searchString = signal('');
  readonly pageNumber = signal(1);
  readonly pageSize = signal(15);
  readonly currentPage = signal<PageViewModel<ShareholdersViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 15,
    data: []
  });

  readonly clientShareClassHolder = signal<ClientShareClassHolder>(undefined);
  readonly shareholdersViewModels = signal<ShareholdersViewModel[]>([]);
  readonly modalDataInput = input.required<ClientShareClassHolder>();

  readonly pagination = viewChild(PaginationComponent);

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.clientShareClassHolder.set(this.modalDataInput());
    this.populateColumns();
    const storedSort = this.sessionStorageService.getSort(SortedTable.ShareHolders);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }

    const holder = this.clientShareClassHolder();
    if (holder?.clientCode) {
      this.shareholdersService.getShareHoldersForClient(holder.clientCode,
        holder.shareClassLineNo,
        holder.shareClass,
        holder.currencyCode).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(shareholderDTOs => {
        const vms = this.converter.convert(shareholderDTOs);
        this.shareholdersViewModels.set(vms);
        this.clientsideDataService.initialize(vms);
        this.updateData();
        this.dataLoading.set(false);
        this.dataEmpty.set(vms.length === 0);
        this.dataAvailable.set(!this.dataEmpty());
      });
    }
  }

  private updateData(): void {
    this.currentPage.set(this.clientsideDataService.generatePage(
      this.sortField(),
      this.sortOrder() === 1,
      this.SEARCH_FIELDS,
      this.searchString(),
      this.pageNumber(),
      this.pageSize()));
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber.set(1);
    this.updateData();
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }
    this.sessionStorageService.setSort(SortedTable.ShareHolders, this.sortField(), this.sortOrder());
  }

  search(searchTerm: string): void {
    this.searchString.set(searchTerm);
    this.pageNumber.set(1);
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber.set(targetPage);
    this.updateData();
  }

  rowClicked(_dataViewModel: DataViewModel): void {
    // Row clicking is not supported in this view
  }

  populateColumns() {
    this.columns.set([
      {
        title: 'Name',
        dataProperty: 'name',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '40%',
        dataFocusable: true,
        headingFocusable: true
      },
      {
        title: 'Total Shares',
        dataProperty: 'totalShares',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '30%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Total Value',
        dataProperty: 'totalValue',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '30%',
        dataFocusable: false,
        headingFocusable: true
      }
    ]);
  }
}
